import {
	Button,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@st-marketplace/ui";
import { useMemo, useState } from "react";
import type { Control, FieldValues, Path } from "react-hook-form";

const DEFAULT_MIN_LENGTH = 10;

type PasswordAlphabet = {
	lower: string;
	upper: string;
	digits: string;
	symbols: string;
};

const DEFAULT_ALPHABET: PasswordAlphabet = {
	lower: "abcdefghijklmnopqrstuvwxyz",
	upper: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
	digits: "0123456789",
	symbols: "!@#$%^&*()-_=+[]{}<>?,.",
};

type PasswordRules = {
	minLength?: number;
	allowSymbols?: boolean;
};

const pickChar = (alphabet: string): string => {
	const idx = crypto.getRandomValues(new Uint32Array(1))[0] % alphabet.length;
	return alphabet[idx]!;
};

const shuffle = (chars: string[]): string[] => {
	// Fisher–Yates using crypto
	for (let i = chars.length - 1; i > 0; i -= 1) {
		const j = crypto.getRandomValues(new Uint32Array(1))[0] % (i + 1);
		[chars[i], chars[j]] = [chars[j]!, chars[i]!];
	}
	return chars;
};

export const generatePassword = (rules: PasswordRules = {}): string => {
	const minLength = rules.minLength ?? DEFAULT_MIN_LENGTH;
	const allowSymbols = rules.allowSymbols ?? true;

	const pools = [
		DEFAULT_ALPHABET.lower,
		DEFAULT_ALPHABET.upper,
		DEFAULT_ALPHABET.digits,
		allowSymbols ? DEFAULT_ALPHABET.symbols : "",
	].filter((p) => p.length > 0);

	// Ensure at least one char from each selected pool
	const required = pools.map((p) => pickChar(p));

	const all = pools.join("");
	const remainingCount = Math.max(minLength - required.length, 0);
	const remaining = Array.from({ length: remainingCount }, () => pickChar(all));

	return shuffle([...required, ...remaining]).join("");
};

type PasswordFieldProps<TForm extends FieldValues> = {
	name: Path<TForm>;
	control: Control<TForm>;
	label: string;
	required?: boolean;
	disabled?: boolean;

	/** If true, shows a "regen" button and initializes with generated password */
	autoGenerate?: boolean;
	minLength?: number;
	allowSymbols?: boolean;

	/** Optional: called when user clicks regen */
	onRegenerate?: (value: string) => void;
	dir?: "ltr" | "rtl" | "auto";
	autoComplete?: string;
};

export const PasswordField = <TForm extends FieldValues>({
	name,
	control,
	label,
	required,
	disabled,
	autoGenerate = false,
	minLength = DEFAULT_MIN_LENGTH,
	allowSymbols = true,
	onRegenerate,
	dir,
	autoComplete,
}: PasswordFieldProps<TForm>) => {
	const [visible, setVisible] = useState(false);

	const initial = useMemo(() => {
		if (!autoGenerate) return "";
		return generatePassword({ minLength, allowSymbols });
	}, [autoGenerate, minLength, allowSymbols]);

	return (
		<FormField
			name={name}
			control={control}
			defaultValue={initial as any}
			render={({ field }) => (
				<FormItem>
					<FormLabel required={required}>{label}</FormLabel>
					<FormControl>
						<Input
							{...field}
							value={field.value ?? ""}
							ltr={dir === "ltr" || dir === undefined}
							type={visible ? "text" : "password"}
							placeholder={label}
							autoComplete={
								autoComplete ?? (autoGenerate ? "new-password" : undefined)
							}
							disabled={disabled}
							onChange={field.onChange}
							append={
								<span className="flex items-center gap-0.5">
									{autoGenerate && (
										<Tooltip>
											<TooltipTrigger asChild>
												<span className="inline-flex">
													<Button
														type="button"
														icon
														variant="text"
														color="ghost"
														size="sm"
														aria-label="Regenerate"
														disabled={disabled}
														onClick={() => {
															const next = generatePassword({
																minLength,
																allowSymbols,
															});
															field.onChange(next);
															onRegenerate?.(next);
														}}
													>
														<Icon name="refresh-line" size="sm" />
													</Button>
												</span>
											</TooltipTrigger>
											<TooltipContent>Regenerate</TooltipContent>
										</Tooltip>
									)}

									<Tooltip>
										<TooltipTrigger asChild>
											<span className="inline-flex">
												<Button
													type="button"
													icon
													variant="text"
													color="ghost"
													size="sm"
													aria-label={visible ? "Hide" : "Show"}
													disabled={disabled}
													onClick={() => setVisible((v) => !v)}
												>
													<Icon
														name={visible ? "eye-off-line" : "eye-line"}
														size="sm"
													/>
												</Button>
											</span>
										</TooltipTrigger>
										<TooltipContent>{visible ? "Hide" : "Show"}</TooltipContent>
									</Tooltip>
								</span>
							}
						/>
					</FormControl>
					<FormMessage />
				</FormItem>
			)}
		/>
	);
};
